import { useRequest } from "@prismicio/editor-support/Suspense"
import { Box, ErrorBoundary, useAlert } from "@prismicio/editor-ui"
import { Suspense, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { z } from "zod"

import { useRouteParams } from "../../app/routing"
import { getAllReleases } from "../../app/services/release"
import { DocumentListLoadingTable } from "../DocumentListBody/DocumentListTable"
import { DocumentListHeader } from "../DocumentListHeader"
import { DocumentListLayout } from "../documentListLayout"
import { DocumentListSidePanel } from "../DocumentListSidePanel"
import { DocumentListProvider } from "../useDocumentList"

export function ReleasePage() {
  return (
    <ErrorBoundary renderError={() => <ReleasePageSkeleton />}>
      <Suspense fallback={<ReleasePageSkeleton />}>
        <ReleasePageLayout />
      </Suspense>
    </ErrorBoundary>
  )
}

function ReleasePageLayout() {
  useRedirectOnNonExistentRelease()

  return (
    <DocumentListProvider>
      <DocumentListLayout
        header={<DocumentListHeader tab="upcoming" />}
        sidePanel={<DocumentListSidePanel />}
        // TODO: Implement ReleasePage body
        // https://linear.app/prismic/issue/PBD-1415/specific-release-page-document-list
        body={<div>ReleasePage</div>}
      />
    </DocumentListProvider>
  )
}

function ReleasePageSkeleton() {
  return (
    <DocumentListProvider>
      <DocumentListLayout
        header={<DocumentListHeader tab="upcoming" />}
        sidePanel={<DocumentListSidePanel />}
        body={
          // TODO: Change skeleton to fit ReleasePage body
          // https://linear.app/prismic/issue/PBD-1415/specific-release-page-document-list
          <Box flexDirection="column" width="100%">
            <Box
              flexGrow={1}
              height="fit-content"
              padding={{ left: 24, top: 24, bottom: 24 }}
              flexDirection="column"
              gap={24}
            >
              <DocumentListLoadingTable />
            </Box>
          </Box>
        }
      />
    </DocumentListProvider>
  )
}

function useRedirectOnNonExistentRelease() {
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  // check if id exists
  const releases = useRequest(getAllReleases, [true])

  const {
    path: { releaseId }
  } = useRouteParams({
    path: { releaseId: z.string() }
  })

  const release = releases.find((release) => release.id === releaseId)

  useEffect(() => {
    if (release) return
    navigate("/upcoming")
    showAlert({
      title: "Release not found",
      subtitle: `The release with id ${releaseId} does not exist. You have been redirected to the upcoming releases page.`
    })
  }, [release, navigate, releaseId, showAlert])
}
